/**
 * 表格列设置组件
 */

import { useState, useEffect, useCallback } from 'react';
import type { AnyObject } from 'antd/es/_util/type';
import { HolderOutlined } from '@ant-design/icons';
import {
  Modal,
  Checkbox,
  Space,
  Button,
  Divider,
  Typography,
} from 'antd';
import { useI18n } from '@/hooks/useI18n';
import { ProTableColumn } from './index';

const { Text } = Typography;

interface ColumnSettingsProps<T = AnyObject> {
  visible: boolean;
  columns: ProTableColumn<T>[];
  /** 重置时恢复的列（页面原始定义） */
  defaultColumns: ProTableColumn<T>[];
  onClose: () => void;
  onChange: (columns: ProTableColumn<T>[]) => void;
}

interface TreeNodeData {
  key: string;
  title: string;
  checkable: boolean;
  disableCheckbox?: boolean;
}

const colKey = <T,>(col: ProTableColumn<T>) => String(col.key ?? col.dataIndex);
const isFixed = <T,>(col: ProTableColumn<T>) => col.fixed === 'left' || col.fixed === 'right';

/**
 * 表格列设置组件
 */
const ColumnSettings = <T extends AnyObject>({
  visible,
  columns,
  defaultColumns,
  onClose,
  onChange,
}: ColumnSettingsProps<T>) => {
  const { t } = useI18n();
  const [localColumns, setLocalColumns] = useState<ProTableColumn<T>[]>(columns);
  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  const [dragKey, setDragKey] = useState<string | null>(null);

  const load = useCallback((cols: ProTableColumn<T>[]) => {
    setLocalColumns([...cols]);
    setCheckedKeys(cols.filter(col => !col.hideInTable).map(colKey));
  }, []);

  // 每次打开按当前列重新加载，取消后未确认的改动不残留
  useEffect(() => {
    if (visible) load(columns);
  }, [columns, visible, load]);

  const handleDrop = (targetKey: string) => {
    if (!dragKey || dragKey === targetKey) return;
    const next = [...localColumns];
    const from = next.findIndex(col => colKey(col) === dragKey);
    const [moved] = next.splice(from, 1);
    next.splice(next.findIndex(col => colKey(col) === targetKey), 0, moved);
    setLocalColumns(next);
  };

  // 生成树形数据
  const generateTreeData = (): TreeNodeData[] => {
    return localColumns.map(col => ({
      key: colKey(col),
      title: String(col.title),
      checkable: true,
      disableCheckbox: isFixed(col), // 固定列不允许隐藏、不参与拖拽
    }));
  };

  // 全选/全不选
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allKeys = localColumns.map(colKey);
      setCheckedKeys(allKeys);
    } else {
      const fixedKeys = localColumns.filter(isFixed).map(colKey);
      setCheckedKeys(fixedKeys);
    }
  };

  // 重置
  const handleReset = () => load(defaultColumns);

  // 确认应用
  const handleConfirm = () => {
    const newColumns = localColumns.map(col => ({
      ...col,
      hideInTable: !checkedKeys.includes(colKey(col)),
    }));
    
    onChange(newColumns);
    onClose();
  };

  // 树形数据
  const treeData = generateTreeData();

  // 计算全选状态
  const selectableCount = localColumns.filter(col => !isFixed(col)).length;
  const checkedSelectableCount = checkedKeys.filter(key => {
    const col = localColumns.find(c => colKey(c) === key);
    return col && !isFixed(col);
  }).length;
  
  const indeterminate = checkedSelectableCount > 0 && checkedSelectableCount < selectableCount;
  const checkAll = checkedSelectableCount === selectableCount;

  return (
    <Modal
      title={t('table.columnSettings')}
      open={visible}
      onCancel={onClose}
      onOk={handleConfirm}
      width={400}
      footer={[
        <Button key="reset" onClick={handleReset}>
          {t('common.reset')}
        </Button>,
        <Button key="cancel" onClick={onClose}>
          {t('common.cancel')}
        </Button>,
        <Button key="confirm" type="primary" onClick={handleConfirm}>
          {t('common.confirm')}
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        {/* 全选操作 */}
        <div style={{ padding: '8px 0' }}>
          <Checkbox
            indeterminate={indeterminate}
            checked={checkAll}
            onChange={(e) => handleSelectAll(e.target.checked)}
          >
            {t('table.selectAll')}
          </Checkbox>
        </div>
        
        <Divider style={{ margin: '8px 0' }} />

        {/* 列列表 */}
        <div style={{ maxHeight: 400, overflow: 'auto' }}>
          {treeData.map(node => (
            <div
              key={node.key}
              data-testid={`column-setting-${node.key}`}
              draggable={!node.disableCheckbox}
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', node.key); // Firefox 不 setData 不会开始拖拽
                setDragKey(node.key);
              }}
              onDragOver={(e) => {
                if (!node.disableCheckbox) e.preventDefault();
              }}
              onDrop={() => handleDrop(node.key)}
              onDragEnd={() => setDragKey(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '4px 0',
                borderBottom: '1px solid #f0f0f0',
                cursor: node.disableCheckbox ? undefined : 'move',
                opacity: dragKey === node.key ? 0.4 : 1,
              }}
            >
              <HolderOutlined style={{ color: node.disableCheckbox ? 'transparent' : '#999' }} />
              <Checkbox
                checked={checkedKeys.includes(node.key)}
                disabled={node.disableCheckbox}
                onChange={(e) => {
                  const newChecked = e.target.checked
                    ? [...checkedKeys, node.key]
                    : checkedKeys.filter(k => k !== node.key);
                  setCheckedKeys(newChecked);
                }}
              >
                <Text 
                  style={{ 
                    color: node.disableCheckbox ? '#999' : undefined 
                  }}
                >
                  {node.title}
                </Text>
              </Checkbox>
            </div>
          ))}
        </div>

        <Divider style={{ margin: '8px 0' }} />

        {/* 提示信息 */}
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t('table.columnSettingsTip')}
        </Text>
      </Space>
    </Modal>
  );
};

export default ColumnSettings;